import { lazy, Suspense, type ReactNode } from "react";
import { createBrowserRouter, Navigate, RouterProvider } from "react-router-dom";
import { Analytics } from "@vercel/analytics/react";
import { SpeedInsights } from "@vercel/speed-insights/react";
import { Layout } from "@/components/site/Layout";
import { HomePage } from "@/pages/HomePage";
import "./index.css";

const PropertiesPage = lazy(() =>
  import("@/pages/PropertiesPage").then((m) => ({ default: m.PropertiesPage })),
);
const PropertyDetailPage = lazy(() =>
  import("@/pages/PropertyDetailPage").then((m) => ({
    default: m.PropertyDetailPage,
  })),
);
const AreasPage = lazy(() =>
  import("@/pages/AreasPage").then((m) => ({ default: m.AreasPage })),
);
const CommunityPage = lazy(() =>
  import("@/pages/CommunityPage").then((m) => ({ default: m.CommunityPage })),
);
const DevelopersPage = lazy(() =>
  import("@/pages/DevelopersPage").then((m) => ({ default: m.DevelopersPage })),
);
const DeveloperPage = lazy(() =>
  import("@/pages/DeveloperPage").then((m) => ({ default: m.DeveloperPage })),
);
const AboutPage = lazy(() =>
  import("@/pages/AboutPage").then((m) => ({ default: m.AboutPage })),
);
const ContactPage = lazy(() =>
  import("@/pages/ContactPage").then((m) => ({ default: m.ContactPage })),
);
const ListPropertyPage = lazy(() =>
  import("@/pages/ListPropertyPage").then((m) => ({
    default: m.ListPropertyPage,
  })),
);
const InsightsPage = lazy(() =>
  import("@/pages/InsightsPage").then((m) => ({ default: m.InsightsPage })),
);
const ArticlePage = lazy(() =>
  import("@/pages/ArticlePage").then((m) => ({ default: m.ArticlePage })),
);
const ComparePage = lazy(() =>
  import("@/pages/ComparePage").then((m) => ({ default: m.ComparePage })),
);
const PrivacyPage = lazy(() =>
  import("@/pages/PrivacyPage").then((m) => ({ default: m.PrivacyPage })),
);
const TermsPage = lazy(() =>
  import("@/pages/TermsPage").then((m) => ({ default: m.TermsPage })),
);
const NotFoundPage = lazy(() =>
  import("@/pages/NotFoundPage").then((m) => ({ default: m.NotFoundPage })),
);

function page(node: ReactNode) {
  return (
    <Suspense
      fallback={
        <div className="container-wide py-16 text-[14px] text-[var(--text-muted)]">
          Loading…
        </div>
      }
    >
      {node}
    </Suspense>
  );
}

const router = createBrowserRouter([
  {
    element: <Layout />,
    children: [
      { path: "/", element: <HomePage /> },
      { path: "/properties", element: page(<PropertiesPage />) },
      { path: "/properties/:id", element: page(<PropertyDetailPage />) },
      { path: "/properties/:id/:slug", element: page(<PropertyDetailPage />) },
      { path: "/areas", element: page(<AreasPage />) },
      { path: "/areas/:slug", element: page(<CommunityPage />) },
      { path: "/developers", element: page(<DevelopersPage />) },
      { path: "/developers/:slug", element: page(<DeveloperPage />) },
      { path: "/about", element: page(<AboutPage />) },
      { path: "/contact", element: page(<ContactPage />) },
      { path: "/list-property", element: page(<ListPropertyPage />) },
      { path: "/insights", element: page(<InsightsPage />) },
      { path: "/insights/:slug", element: page(<ArticlePage />) },
      // Legacy: keep the old /faq URL working by redirecting to /insights.
      { path: "/faq", element: <Navigate to="/insights" replace /> },
      { path: "/compare", element: page(<ComparePage />) },
      { path: "/privacy", element: page(<PrivacyPage />) },
      { path: "/terms", element: page(<TermsPage />) },
      { path: "*", element: page(<NotFoundPage />) },
    ],
  },
]);

export function App() {
  return (
    <>
      <RouterProvider router={router} />
      <Analytics />
      <SpeedInsights />
    </>
  );
}

export default App;
